import { createFileRoute } from "@tanstack/react-router";
import { format, formatDistanceToNowStrict, parseISO } from "date-fns";
import { CheckCircle2, MessageCircle, PenLine, ThumbsUp, Trash2 } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { useConfirm } from "@/components/layout/confirm-provider";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DraftEditor, useCreateDraft, useDeleteDraft, useMyDrafts } from "@/features/drafts";
import type { DraftPublic } from "@/lib/api";

/**
 * The employee's drafts: things they are writing, and things they have posted.
 *
 * Private to them. A draft is written by them, from a prompt or a moment or
 * from nothing. Once posted, its likes and comments appear here and keep
 * updating on their own.
 */
export const Route = createFileRoute("/_authenticated/me/drafts")({
  component: DraftsPage,
});

function DraftsPage() {
  const { data, isPending, isError, refetch } = useMyDrafts();
  const create = useCreateDraft();
  const [openId, setOpenId] = useState<string | null>(null);

  if (isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  const open = data.find((d) => d.id === openId) ?? null;
  const writing = data.filter((d) => d.status === "draft");
  const posted = data.filter((d) => d.status === "posted");

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="My space"
        title="Drafts"
        description="What you are writing, and what you have posted. Yours alone."
        actions={
          <Button
            onClick={() => create.mutate({ body: {} }, { onSuccess: (d) => setOpenId(d.id) })}
            disabled={create.isPending}
          >
            <PenLine /> Blank draft
          </Button>
        }
      />

      {data.length === 0 ? (
        <EmptyState
          title="Nothing written yet"
          description="Start from a prompt on This month, from a moment, or write one from scratch."
        />
      ) : (
        <div className="space-y-6">
          {writing.length > 0 ? (
            <Section title="Writing" count={writing.length}>
              {writing.map((d) => (
                <DraftRow key={d.id} draft={d} onOpen={() => setOpenId(d.id)} />
              ))}
            </Section>
          ) : null}
          {posted.length > 0 ? (
            <Section title="Posted" count={posted.length}>
              {posted.map((d) => (
                <DraftRow key={d.id} draft={d} onOpen={() => setOpenId(d.id)} />
              ))}
            </Section>
          ) : null}
        </div>
      )}

      <DraftEditor draft={open} onClose={() => setOpenId(null)} />
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 font-semibold text-foreground text-sm uppercase tracking-[0.08em]">
        {title} <Badge variant="secondary">{count}</Badge>
      </h2>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}

function DraftRow({ draft, onOpen }: { draft: DraftPublic; onOpen: () => void }) {
  const confirm = useConfirm();
  const remove = useDeleteDraft();
  const posted = draft.status === "posted";
  const preview = draft.body.trim() || "Empty draft";

  return (
    <li>
      <Card className="transition-colors hover:border-primary/40">
        <CardContent className="flex items-start justify-between gap-3">
          <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
            {draft.title ? (
              <p className="font-medium text-foreground text-sm">{draft.title}</p>
            ) : null}
            <p className="line-clamp-2 text-muted-foreground text-sm">{preview}</p>
            {!posted ? (
              <span className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
                {draft.promptFramework ? (
                  <Badge variant="outline">{draft.promptFramework}</Badge>
                ) : (
                  <Badge variant="outline">From scratch</Badge>
                )}
                <span>
                  {draft.body.trim()
                    ? `${draft.body.trim().length} characters`
                    : "Nothing written yet"}
                </span>
                <span>· Edited {formatDistanceToNowStrict(parseISO(draft.updatedAt))} ago</span>
              </span>
            ) : null}
            {posted && draft.reactions != null ? (
              <div className="mt-2 flex items-center gap-3 text-muted-foreground text-xs">
                <Badge variant="success">
                  <CheckCircle2 /> Posted{" "}
                  {draft.postedAt ? format(parseISO(draft.postedAt), "d MMM") : ""}
                </Badge>
                <span className="flex items-center gap-1">
                  <ThumbsUp className="size-3.5" /> {draft.reactions ?? "?"}
                </span>
                <span className="flex items-center gap-1">
                  <MessageCircle className="size-3.5" /> {draft.comments ?? "?"}
                </span>
              </div>
            ) : posted ? (
              <p className="mt-2 text-muted-foreground text-xs">
                Posted. First numbers arriving shortly.
              </p>
            ) : null}
          </button>
          {!posted ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Delete this draft"
              className="text-muted-foreground hover:text-destructive-strong"
              onClick={async () => {
                const ok = await confirm({
                  title: "Delete this draft?",
                  description: "The text goes with it. This cannot be undone.",
                  confirmLabel: "Delete draft",
                  destructive: true,
                });
                if (ok) remove.mutate({ path: { draft_id: draft.id } });
              }}
            >
              <Trash2 />
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </li>
  );
}
